import type { BillingInterval, CustomerType, PurchaseAttribution } from '@api/policy/billing';

const STORAGE_KEY = 'jooice_checkout_intent';
const VALID_INTERVALS: readonly BillingInterval[] = ['month', 'year'];
export type CheckoutMarket = 'ita' | 'usa';
const VALID_MARKETS: readonly CheckoutMarket[] = ['ita', 'usa'];

export type CheckoutIntent = {
  plan?: string;
  interval?: BillingInterval;
};
export type PendingPurchaseIntent = {
  planKey: string;
  interval: BillingInterval;
  market: CheckoutMarket;
  /** Explicit buyer choice; never absent. FE sends it in the Policy checkout body only (C2). */
  marketingConsent: boolean;
  /** ita billing form values; agentCode absent once the buyer clears it. */
  customerType?: CustomerType;
  taxCode?: string;
  agentCode?: string;
};
/** Commercial codes and attribution from the WordPress redirect, kept across the sign-in detour. */
export type PurchaseContext = {
  agentCode?: string;
  partnerKey?: string;
  referrerCode?: string;
  attribution?: PurchaseAttribution;
};

const PENDING_PURCHASE_KEY = 'jooice_pending_purchase';
const PURCHASE_CONTEXT_KEY = 'jooice_purchase_context';
const VALID_CUSTOMER_TYPES: readonly CustomerType[] = ['company', 'freelancer'];
const CODE_MAX_LENGTH = 64;
const TAX_CODE_LENGTH = 16;
const ATTRIBUTION_MAX_LENGTH = 255;
const CODE_PARAMS = [
  ['agent_code', 'agentCode'],
  ['partner_key', 'partnerKey'],
  ['referrer_code', 'referrerCode'],
] as const;
const ATTRIBUTION_PARAMS = [
  ['utm_source', 'utmSource'],
  ['utm_medium', 'utmMedium'],
  ['utm_campaign', 'utmCampaign'],
  ['utm_content', 'utmContent'],
  ['utm_term', 'utmTerm'],
  ['gclid', 'gclid'],
  ['wbraid', 'wbraid'],
  ['gbraid', 'gbraid'],
] as const;

/** Trimmed non-empty string within maxLength, else undefined (absent means unknown). */
const boundedString = (value: unknown, maxLength: number): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed && trimmed.length <= maxLength ? trimmed : undefined;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Keeps only bounded string codes and attribution keys; drops everything else. */
const sanitizePurchaseContext = (raw: unknown): PurchaseContext => {
  const source = isRecord(raw) ? raw : {};
  const attributionSource = isRecord(source.attribution) ? source.attribution : {};
  const context: PurchaseContext = {};
  for (const [, key] of CODE_PARAMS) {
    const value = boundedString(source[key], CODE_MAX_LENGTH);
    if (value) context[key] = value;
  }
  const attribution: PurchaseAttribution = {};
  for (const [, key] of ATTRIBUTION_PARAMS) {
    const value = boundedString(attributionSource[key], ATTRIBUTION_MAX_LENGTH);
    if (value) attribution[key] = value;
  }
  if (Object.keys(attribution).length) context.attribution = attribution;
  return context;
};

// Values are forwarded as claims and validated by Policy; the FE only enforces format and length.
// Last touch: a visit carrying any value replaces the stored context, a visit without keeps it.
export function capturePurchaseContext(searchParams: URLSearchParams): void {
  const fromQuery = (params: typeof CODE_PARAMS | typeof ATTRIBUTION_PARAMS) =>
    Object.fromEntries(params.map(([param, key]) => [key, searchParams.get(param)]));
  const context = sanitizePurchaseContext({ ...fromQuery(CODE_PARAMS), attribution: fromQuery(ATTRIBUTION_PARAMS) });
  if (!Object.keys(context).length) return;
  try {
    window.sessionStorage.setItem(PURCHASE_CONTEXT_KEY, JSON.stringify(context));
  } catch {
    // sessionStorage unavailable: the context is optional.
  }
}

export function getPurchaseContext(): PurchaseContext {
  try {
    return sanitizePurchaseContext(JSON.parse(window.sessionStorage.getItem(PURCHASE_CONTEXT_KEY) ?? 'null'));
  } catch {
    return {};
  }
}

export function persistPendingPurchase(intent: PendingPurchaseIntent): boolean {
  try {
    window.sessionStorage.setItem(PENDING_PURCHASE_KEY, JSON.stringify(intent));
    return true;
  } catch {
    return false;
  }
}
export function getPendingPurchase(): PendingPurchaseIntent | null {
  try {
    const raw = window.sessionStorage.getItem(PENDING_PURCHASE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingPurchaseIntent>;
    if (
      typeof parsed.planKey !== 'string' ||
      !parsed.planKey.trim() ||
      !VALID_INTERVALS.includes(parsed.interval as BillingInterval) ||
      !VALID_MARKETS.includes(parsed.market as CheckoutMarket) ||
      typeof parsed.marketingConsent !== 'boolean'
    ) {
      return null;
    }
    const intent: PendingPurchaseIntent = {
      planKey: parsed.planKey.trim().toLowerCase(),
      interval: parsed.interval as BillingInterval,
      market: parsed.market as CheckoutMarket,
      marketingConsent: parsed.marketingConsent,
    };
    if (VALID_CUSTOMER_TYPES.includes(parsed.customerType as CustomerType)) {
      intent.customerType = parsed.customerType;
    }
    const taxCode = boundedString(parsed.taxCode, TAX_CODE_LENGTH);
    if (taxCode) intent.taxCode = taxCode;
    const agentCode = boundedString(parsed.agentCode, CODE_MAX_LENGTH);
    if (agentCode) intent.agentCode = agentCode;
    return intent;
  } catch {
    return null;
  }
}

export function clearPendingPurchase(): void {
  try {
    window.sessionStorage.removeItem(PENDING_PURCHASE_KEY);
    window.sessionStorage.removeItem(PURCHASE_CONTEXT_KEY);
  } catch {
    // Nothing to clear when sessionStorage is unavailable.
  }
}

// URL values are selection hints only: plan is matched against Policy-returned sellable plans by the caller.
// Price IDs are never read; utm_* and commercial codes go through capturePurchaseContext. market is a validated commercial selection (ita|usa only), never identity.
export function captureCheckoutIntent(searchParams: URLSearchParams): void {
  const plan = searchParams.get('plan')?.trim().toLowerCase() || undefined;
  const rawInterval = searchParams.get('interval')?.trim().toLowerCase() as BillingInterval | undefined;
  const interval = rawInterval && VALID_INTERVALS.includes(rawInterval) ? rawInterval : undefined;
  if (!plan && !interval) return;

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ plan, interval }));
  } catch {
    // sessionStorage unavailable: checkout intent is optional.
  }
}

export function getCheckoutIntent(): CheckoutIntent | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CheckoutIntent;
    const plan = typeof parsed.plan === 'string' ? parsed.plan : undefined;
    const interval = VALID_INTERVALS.find((value) => value === parsed.interval);
    return plan || interval ? { plan, interval } : null;
  } catch {
    return null;
  }
}

export function clearCheckoutIntent(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // sessionStorage unavailable: nothing to clear.
  }
}

/** Reads the WP-passed market (ita|usa). Other values are unsupported; there is no MEA purchase UX. */
export function readPurchaseMarket(searchParams: URLSearchParams): CheckoutMarket | null {
  const raw = searchParams.get('market')?.trim().toLowerCase();
  return VALID_MARKETS.find((value) => value === raw) ?? null;
}

export function purchaseQueryParams(searchParams: URLSearchParams): URLSearchParams {
  const params = new URLSearchParams();
  for (const key of ['planKey', 'interval', 'market'] as const) {
    const value = searchParams.get(key);
    if (value) params.set(key, value);
  }
  return params;
}
